import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, TextField, Button, 
  List, ListItem, ListItemText, IconButton, Divider, 
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase-config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const SimpleQAEditor = () => {
  const { currentUser } = useAuth();
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [saveLoading, setSaveLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch business data on component mount
  useEffect(() => {
    console.log('SimpleQAEditor component mounted');
    const fetchBusinessData = async () => {
      if (!currentUser) {
        setLoading(false);
        setError('You must be logged in to access this page');
        return;
      }
      
      try {
        console.log('Fetching business data for user:', currentUser.uid);
        const businessDoc = await getDoc(doc(db, 'businesses', currentUser.uid));
        
        if (businessDoc.exists()) {
          const data = businessDoc.data();
          console.log('Business data found:', data);
          setBusinessData(data);
          
          // Extract common questions if they exist
          if (data.commonQuestions && Array.isArray(data.commonQuestions)) {
            console.log('Common questions found:', data.commonQuestions);
            setQuestions(data.commonQuestions);
          }
        } else {
          console.log('No business document found');
          setError('No business information found.');
        }
      } catch (err) {
        console.error('Error fetching business data:', err);
        setError('Failed to load business information');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBusinessData();
  }, [currentUser]);

  // Handle opening the add/edit dialog
  const handleOpenDialog = (index = -1) => {
    if (index >= 0) {
      // Edit existing question
      setCurrentQuestion(questions[index].question || '');
      setCurrentAnswer(questions[index].answer || '');
      setEditIndex(index);
    } else {
      // Add new question
      setCurrentQuestion('');
      setCurrentAnswer('');
      setEditIndex(-1);
    }
    setDialogOpen(true);
  };

  // Handle closing the dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentQuestion('');
    setCurrentAnswer('');
    setEditIndex(-1);
  };

  // Handle saving a question
  const handleSaveQuestion = () => {
    if (!currentQuestion.trim() || !currentAnswer.trim()) {
      setSnackbarMessage('Question and answer cannot be empty');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    const newQuestions = [...questions];
    const questionItem = {
      question: currentQuestion.trim(),
      answer: currentAnswer.trim()
    };
    
    if (editIndex >= 0) {
      // Update existing question
      newQuestions[editIndex] = questionItem;
      setSnackbarMessage('Question updated successfully');
    } else {
      // Add new question
      newQuestions.push(questionItem);
      setSnackbarMessage('Question added successfully');
    }
    
    setQuestions(newQuestions);
    setHasChanges(true);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    handleCloseDialog();
  };

  // Handle deleting a question
  const handleDeleteQuestion = (index) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      const newQuestions = [...questions];
      newQuestions.splice(index, 1);
      setQuestions(newQuestions);
      setHasChanges(true);
      setSnackbarMessage('Question deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }
  };

  // Handle saving all changes to Firestore
  const handleSaveChanges = async () => {
    if (!currentUser) {
      setSnackbarMessage('You must be logged in to save changes');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    setSaveLoading(true);
    
    try {
      console.log('Saving changes to Firestore for user:', currentUser.uid);
      console.log('Questions to save:', questions);
      
      const businessRef = doc(db, 'businesses', currentUser.uid);
      
      // Update only the commonQuestions field and updatedAt
      await updateDoc(businessRef, {
        commonQuestions: questions,
        updatedAt: new Date().toISOString()
      });
      
      setHasChanges(false);
      setSnackbarMessage('Changes saved successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error saving changes:', err);
      setSnackbarMessage('Failed to save changes: ' + err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle going back to test interface
  const handleBackToTest = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return;
    }
    window.location.href = '/test-interface';
  };

  // Handle going to home
  const handleGoHome = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return;
    }
    window.location.href = '/';
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button 
            variant="contained" 
            sx={{ mt: 2 }}
            onClick={() => window.location.href = '/'}
          >
            Go to Home
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 3 }}>
        <Typography variant="h3" component="h1">
          Manage Q&A
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
          >
            Home
          </Button>
          <Button 
            variant="contained" 
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToTest}
            color="primary"
          >
            Back to Test Interface
          </Button>
        </Box>
      </Box>
      
      <Typography variant="subtitle1" gutterBottom>
        Add, edit, or remove questions and answers for your AI assistant. These Q&A items will be used to train your AI to respond to customer inquiries.
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        You can return to this page at any time to update your AI's knowledge base, even months after initial setup.
      </Typography>
      
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Common Questions & Answers
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            color="primary"
          >
            Add New Q&A
          </Button>
        </Box>
        
        {questions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No questions added yet. Click "Add New Q&A" to get started.
            </Typography>
          </Box>
        ) : (
          <List>
            {questions.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem
                  secondaryAction={
                    <Box>
                      <IconButton edge="end" onClick={() => handleOpenDialog(index)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleDeleteQuestion(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={item.question}
                    secondary={item.answer}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                    secondaryTypographyProps={{ 
                      sx: { 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        maxWidth: '80%'
                      } 
                    }}
                  />
                </ListItem>
                {index < questions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveChanges}
            disabled={saveLoading || !hasChanges}
            sx={{ minWidth: 120 }}
          >
            {saveLoading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </Box>
      </Paper>
      
      {/* Add/Edit Question Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editIndex >= 0 ? 'Edit Question & Answer' : 'Add New Question & Answer'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Question"
            fullWidth
            variant="outlined"
            value={currentQuestion}
            onChange={(e) => setCurrentQuestion(e.target.value)}
            placeholder="e.g., What are your business hours?"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Answer"
            fullWidth
            variant="outlined"
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            placeholder="e.g., Our business hours are 9am-5pm Monday through Friday."
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveQuestion} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SimpleQAEditor; 